import { useCallback, useEffect, useState } from "react";
import { getPlantData } from "../../../../../AllEquipments/CommonFeatures/PMSTab2/4d/api";
import { patchTaskSiInList } from "../../../../../AllEquipments/CommonFeatures/MCS3/Schedule4D/siUtils";
import {
  buildRowsFromTasksForEditableTable,
  normalizeDescription,
} from "./taskFormatters";
import {
  resolveScheduleTaskType,
  taskMatchesScheduleType,
} from "./constants";

export const useScheduleTaskData = ({
  selectedPlant,
  accessToken,
  schedule4D,
  setSnackbar,
  fixedType,
}) => {
  const scheduleTaskType = resolveScheduleTaskType(fixedType);
  const [scheduleTasks, setScheduleTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);

  const loadScheduleTasks = useCallback(async () => {
    const actualPlantId =
      selectedPlant?.id || selectedPlant?._id || selectedPlant?.plantId;

    if (!actualPlantId) {
      setScheduleTasks([]);
      setAllTasks([]);
      return;
    }

    try {
      setTasksLoading(true);

      const tasks = await getPlantData(actualPlantId, accessToken);
      console.log("4D tasks (plant)", tasks);

      let tasksArray = Array.isArray(tasks) ? tasks : [];

      if (
        schedule4D?.schedulePlantId === actualPlantId &&
        Array.isArray(schedule4D?.scheduleMaterials) &&
        schedule4D.scheduleMaterials.length > 0
      ) {
        const byScheduleTaskId = new Map(
          schedule4D.scheduleMaterials.map((row) => [
            row.id ?? row.scheduleTaskId,
            row,
          ])
        );

        tasksArray = tasksArray.map((task) => {
          const sch = byScheduleTaskId.get(task.id);
          if (!sch) return task;

          return {
            ...task,
            mrqMaterials: sch.mrqMaterials ?? task.mrqMaterials,
            mrvMaterials: sch.mrvMaterials ?? task.mrvMaterials,
          };
        });
      }

      const map = schedule4D?.procurementProgressMap ?? new Map();
      const avgProgress = schedule4D?.procurementCalculatedProgress;
      const actualStartMap = schedule4D?.procurementActualStartMap ?? new Map();
      const actualEndMap = schedule4D?.procurementActualEndMap ?? new Map();
      const budgetedCostMap =
        schedule4D?.procurementBudgetedCostMap ?? new Map();
      const actualCostMap = schedule4D?.procurementActualCostMap ?? new Map();

      if (
        schedule4D?.schedulePlantId === actualPlantId &&
        (map.size > 0 ||
          avgProgress != null ||
          actualStartMap.size > 0 ||
          actualEndMap.size > 0 ||
          budgetedCostMap.size > 0 ||
          actualCostMap.size > 0)
      ) {
        tasksArray = tasksArray.map((task) => {
          const type = (task?.type || "").toLowerCase();
          if (type !== "procurement") return task;

          const taskCode = normalizeDescription(task?.taskCode ?? "");
          const taskDesc = normalizeDescription(task?.task ?? task?.name ?? "");

          const progress =
            (taskCode && map.get(taskCode)) ??
            (taskDesc && map.get(taskDesc)) ??
            avgProgress ??
            task?.progress ??
            0;

          const actualStart =
            (taskCode && actualStartMap.get(taskCode)) ??
            (taskDesc && actualStartMap.get(taskDesc)) ??
            task?.actualStart ??
            null;

          const actualEnd =
            (taskCode && actualEndMap.get(taskCode)) ??
            (taskDesc && actualEndMap.get(taskDesc)) ??
            task?.actualEnd ??
            null;

          const budgetedCost =
            (taskCode && budgetedCostMap.get(taskCode)) ??
            (taskDesc && budgetedCostMap.get(taskDesc)) ??
            null;

          const actualCost =
            (taskCode && actualCostMap.get(taskCode)) ??
            (taskDesc && actualCostMap.get(taskDesc)) ??
            null;

          return {
            ...task,
            progress: Number(progress),
            actualStart: actualStart || task?.actualStart,
            actualEnd: actualEnd || task?.actualEnd,
            estimatedTime: budgetedCost ?? task?.estimatedTime,
            manhours: actualCost ?? task?.manhours,
          };
        });
      }

      setAllTasks(tasksArray);

      const typeTasksArray = tasksArray.filter((task) =>
        taskMatchesScheduleType(task, scheduleTaskType)
      );

      const rows = buildRowsFromTasksForEditableTable(typeTasksArray);
      console.log(`${scheduleTaskType} schedule rows`, rows);

      setScheduleTasks(rows);
    } catch (error) {
      console.error("Error loading plant schedule tasks:", error);

      setScheduleTasks([]);
      setAllTasks([]);

      setSnackbar({
        open: true,
        message: error?.message || "Failed to load schedule tasks",
        severity: "error",
      });
    } finally {
      setTasksLoading(false);
    }
  }, [selectedPlant, accessToken, schedule4D, setSnackbar, scheduleTaskType]);

  useEffect(() => {
    loadScheduleTasks();
  }, [loadScheduleTasks]);

  const patchTaskSi = useCallback((taskId, si) => {
    setScheduleTasks((prev) => patchTaskSiInList(prev, taskId, si));
    setAllTasks((prev) => patchTaskSiInList(prev, taskId, si));
  }, []);

  return {
    scheduleTasks,
    allTasks,
    tasksLoading,
    loadScheduleTasks,
    patchTaskSi,
  };
};
